import * as React from 'react'
import { useNavigate } from 'react-router-dom-v5-compat';

import { alphabeticalName, } from '../../../../../../modules/sortingMethods'
import { ClassroomInterface, SkillCategoryInterface, StudentPerformanceData } from '../interfaces';
import { useReportPremiumContext } from '../../shared/report_premium_context';
import {
  Spinner,
  EXTRA_SMALL_ICON_BASE_SRC,
  Tooltip,
  pluralize,
  titleCaseMultiWordString,
} from '../../../../../Shared/index';
import { NEEDS_MORE_PRACTICE, MEETING_GOAL, APPROACHING_GOAL, NEEDS_SUPPORT, getSmartGroup, smartGroupToClassName, formatLastPracticed, formatMomentumScore, getSortableSmartGroup, ORDERED_SMART_GROUPS, extraSmallLockIcon, } from '../../shared/shared';

const helpIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/help.svg`;

interface ByStudentProps {
  classroom: ClassroomInterface;
  data: StudentPerformanceData[] | null;
  handleAssignTargetedPracticeClick: (skillId: number, studentIds: number[], smartGroup?: string) => void;
  momentumScoreGoal: number;
  selectedSkillCategory: SkillCategoryInterface;
  selectedSkillSubCategory: SkillCategoryInterface | null;
}

interface StudentCardProps {
  student: StudentPerformanceData;
  momentumScoreGoal: number;
  smartGroup: string;
  className?: string;
}

const StudentCard: React.FC<StudentCardProps> = ({ student, momentumScoreGoal, smartGroup, className = '' }) => {
  const { name, exposure, ema, last_practiced, total_answered, } = student

  const score = Math.round(ema);
  const lastPracticed = formatLastPracticed(last_practiced);

  const scoreClassName = smartGroup === NEEDS_MORE_PRACTICE ? smartGroupToClassName[getSmartGroup(exposure, ema, total_answered, momentumScoreGoal, true)] : smartGroupToClassName[smartGroup];

  return (
    <section className={`smart-group-card ${className}`}>
      <div className="name-and-last-practiced">
        <h3>
          {name}
        </h3>
        <p className="student-practice-info">
          Last practiced on {lastPracticed}
        </p>
      </div>
      <div className="smart-group-metrics">
        <div className="metric">
          <h4 className="metric-label">Momentum Score</h4>
          <span className={`metric-value score ${scoreClassName}`}>
            {formatMomentumScore(score, exposure)}
          </span>
        </div>
        <div className="metric">
          <h4 className="metric-label">Exposure</h4>
          <span className={`metric-value exposure ${exposure.toLowerCase()}`}>
            {exposure}
          </span>
        </div>
      </div>
    </section>
  );
};

interface SmartGroupSectionProps {
  classPercentage: number;
  momentumScoreGoal: number;
  onClickAssignPractice: () => void;
  selectedSkillCategory?: SkillCategoryInterface;
  selectedSkillSubCategory?: SkillCategoryInterface | null;
  smartGroup: string;
  students: StudentPerformanceData[];
  totalStudents: number;
}

const SmartGroupSection: React.FC<SmartGroupSectionProps> = ({
  classPercentage,
  momentumScoreGoal,
  onClickAssignPractice,
  selectedSkillSubCategory,
  smartGroup,
  students,
  totalStudents,
}) => {
  const { hasSchoolOrDistrictPremium } = useReportPremiumContext();
  const [showAllStudents, setShowAllStudents] = React.useState(false);

  // Sort students by Momentum Score (highest to lowest) then by name
  const sortedStudents = React.useMemo(() => {
    return [...students].sort((a, b) => {
      const aSort = getSortableSmartGroup(smartGroup, a.ema, alphabeticalName(a.name));
      const bSort = getSortableSmartGroup(smartGroup, b.ema, alphabeticalName(b.name));
      return aSort.localeCompare(bSort);
    });
  }, [students, smartGroup]);

  const toggleShowAll = () => {
    setShowAllStudents(!showAllStudents);
  };

  const getTooltipText = (goal: number, skillCategoryOrSubCategory: string) => {
    switch (smartGroup) {
      case MEETING_GOAL:
        return `Students whose Momentum Score for the selected ${skillCategoryOrSubCategory} meets or exceeds your goal of ${goal} and have medium or high exposure. You can change this goal at any time by clicking the 'Change goal' button.`;
      case APPROACHING_GOAL:
        return `Students with a Momentum Score between ${goal - 10} and ${goal - 1} for the selected ${skillCategoryOrSubCategory} and medium or high exposure. They are making steady progress.`;
      case NEEDS_SUPPORT:
        return `Students with a Momentum Score below ${goal - 10} for the selected ${skillCategoryOrSubCategory} despite medium or high exposure. They may need targeted intervention.`;
      case NEEDS_MORE_PRACTICE:
        return `Students with low exposure (5 or fewer questions answered) for the selected ${skillCategoryOrSubCategory}, regardless of their score. Their Momentum Score is not yet reliable.`;
      default:
        return '';
    }
  };

  const skillCategoryOrSubCategory = selectedSkillSubCategory ? 'skill sub-category' : 'skill category';
  const tooltipText = getTooltipText(momentumScoreGoal, skillCategoryOrSubCategory);

  return (
    <section className={`smart-group-section ${showAllStudents ? 'show-all' : ''}`}>
      <div className="smart-group-header">
        <div className="smart-group-title">
          <h2>{titleCaseMultiWordString(smartGroup)}</h2>
          <Tooltip
            tooltipText={tooltipText}
            tooltipTriggerText={<img alt="Help icon" src={helpIconSrc} />}
          />
        </div>
        <div className="smart-group-stats">
          <span>{totalStudents} {pluralize(totalStudents, 'student', 'students')}</span>
          <span aria-hidden={true}>•</span>
          <span>{classPercentage}% of class</span>
          <button className="quill-button outlined small grey focus-on-light" onClick={onClickAssignPractice} type="button">{!hasSchoolOrDistrictPremium && extraSmallLockIcon}Assign practice</button>
        </div>
      </div>
      <div className="smart-group-cards-grid">
        {sortedStudents.map((student, index) => (
          <StudentCard
            className={index >= 4 ? 'initially-hidden' : ''}
            key={student.id}
            momentumScoreGoal={momentumScoreGoal}
            smartGroup={smartGroup}
            student={student}
          />
        ))}
      </div>
      {students.length > 4 && (
        <button 
          className="quill-button focus-on-light small outlined green" 
          onClick={toggleShowAll}
          type="button"
        >
          {showAllStudents ? 'Show fewer students' : `Show all ${students.length} students`}
        </button>
      )}
    </section>
  );
};

const ByStudent: React.FC<ByStudentProps> = ({
  classroom,
  data,
  handleAssignTargetedPracticeClick,
  momentumScoreGoal,
  selectedSkillCategory,
  selectedSkillSubCategory,
}) => {
  const navigate = useNavigate();
  if (!data) { return <Spinner /> }

  // Group students by smart group - only those who have practiced
  const studentsWithPractice = data.filter(student => student.total_answered > 0);
  const studentsByGroup = studentsWithPractice.reduce((groups, student) => {
    const smartGroup = getSmartGroup(student.exposure, student.ema, student.total_answered, momentumScoreGoal);
    if (!groups[smartGroup]) {
      groups[smartGroup] = [];
    }
    groups[smartGroup].push(student);
    return groups;
  }, {} as Record<string, StudentPerformanceData[]>);

  const totalStudentsInClass = classroom?.students?.length || data.length;
  
  const getClassPercentage = (count: number) => Math.round((count / totalStudentsInClass) * 100);
  
  const studentsWhoHaventPracticed = data.filter(student => student.total_answered === 0);
  const hasUnpracticedStudents = studentsWhoHaventPracticed.length > 0;
  const allStudentsUnpracticed = studentsWhoHaventPracticed.length === data.length;

  const handleViewAllStudents = () => {
    navigate(`/teachers/progress_reports/skills_report/classroom/${classroom.id}/performance_breakdown/by_student`);
  };

  const sharedProps = {
    momentumScoreGoal,
    selectedSkillCategory,
    selectedSkillSubCategory,
  }

  if (!classroom.students?.length) {
    return (
      <div className="by-student">
        <div className="empty-state">
          <h2>
            There are no students in the selected class
          </h2>
          <a 
            className="quill-button contained medium green focus-on-light"
            href="/teachers/classrooms"
          >
            Add students
          </a>
        </div>
      </div>
    )
  }

  if (allStudentsUnpracticed) {
    return (
      <div className='empty-state'>
        <h2>
          No students have practiced this {selectedSkillSubCategory ? 'skill sub-category' : 'skill category'} yet
        </h2>
        <button 
          className="quill-button contained medium green focus-on-light"
          onClick={handleViewAllStudents}
          type="button"
        >
          View all students in Performance Breakdown
        </button>
      </div>
    )
  }

  const smartGroupSections = ORDERED_SMART_GROUPS.map(smartGroup => {
    if (!(studentsByGroup[smartGroup]?.length > 0)) { return }

    function onClickAssignPractice() {
      const skillId = selectedSkillSubCategory ? selectedSkillSubCategory.id : selectedSkillCategory.id
      const studentIds = studentsByGroup[smartGroup].map(student => student.id)
      handleAssignTargetedPracticeClick(skillId, studentIds, smartGroup)
    }

    return (
      <SmartGroupSection
        {...sharedProps}
        classPercentage={getClassPercentage(studentsByGroup[smartGroup].length)}
        key={smartGroup}
        onClickAssignPractice={onClickAssignPractice}
        smartGroup={smartGroup}
        students={studentsByGroup[smartGroup]}
        totalStudents={studentsByGroup[smartGroup].length}
      />
    )
  }).filter(Boolean)
  

  return (
    <div className="by-student">
      {smartGroupSections}
      {hasUnpracticedStudents && (
        <section className="unpracticed-section-wrapper">
          <div className='unpracticed-section'>
            <h2>
              {studentsWhoHaventPracticed.length} {pluralize(studentsWhoHaventPracticed.length, 'student', 'students')} {pluralize(studentsWhoHaventPracticed.length, "hasn't", "haven't")} practiced this {
                selectedSkillSubCategory ? 'skill sub-category' : 'skill category'
              } yet
            </h2>
            <button 
              className="quill-button outlined medium grey focus-on-light"
              onClick={handleViewAllStudents}
              type="button"
            >
              View all students in Performance Breakdown
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

export default ByStudent
